import { motion } from "framer-motion";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FaqSection } from "../sections/FaqSection";
import { CONTACT, IMAGES } from "../data/content";
import { Award, Users, ShieldCheck, Trophy, Check } from "lucide-react";
import { useUI } from "../context/UIContext";

const VALUES = [
  { icon: Award, title: "Über 15 Jahre Erfahrung", text: "Langjährige Expertise in Energieberatung und Tarifoptimierung für Privat und Gewerbe." },
  { icon: Users, title: "Deutschlandweit", text: "Persönliche Betreuung von Hamburg bis München – für jede Kundengröße." },
  { icon: ShieldCheck, title: "Unabhängig & ehrlich", text: "Keine Portalprovisionen, keine Tariftricks. Nur Ihr wirtschaftlicher Vorteil zählt." },
];

export default function UeberUns() {
  const { openLead } = useUI();
  return (
    <>
      <Seo title="Über uns" description="Kerstin Hemming – unabhängige Energietarifberaterin. Persönlich, ehrlich und deutschlandweit für Sie im Einsatz." path="/ueber-uns" />
      <PageHero
        eyebrow="Über uns"
        titleLines={["Ihre Energie", 'ist unser <span class="font-serif italic text-blue-700">Antrieb.</span>']}
        subtitle="Persönliche, unabhängige Beratung mit Blick auf langfristige Kostenersparnis."
        breadcrumb={[{ label: "Über uns" }]}
        image={IMAGES.office}
      />

      <section className="bg-background py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-16 items-center">
          <Reveal>
            <div className="rounded-[2rem] overflow-hidden border border-slate-200">
              <img src="/kerstin-hemming.jpg" alt="Kerstin Hemming – Unabhängige Energietarifberaterin" className="w-full h-[520px] object-cover object-top" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-blue-700 mb-5">{CONTACT.responsible}</p>
            <h2 className="text-4xl sm:text-5xl font-heading font-semibold text-navy-900 leading-[1.05] mb-8">
              Kerstin Hemming
            </h2>
            <div className="space-y-5 text-slate-600 leading-relaxed">
              <p>
                „Ich berate Gewerbe- und Privatkunden unabhängig zu Energieverträgen – persönlich, transparent und mit Blick auf langfristige Kostenersparnis. Wenn Sie möchten, prüfe ich Ihre letzte Abrechnung und zeige Ihnen ehrlich, ob und wo sich Optimierungen lohnen."
              </p>
              <p>
                Durch börsennahe Beschaffung über passende Partner können sich oft deutliche Ersparnisse ergeben. Kein Portal-Dschungel, sondern direkte, ehrliche Beratung.
              </p>
            </div>
            <button
              onClick={() => openLead({ type: "beratung" })}
              className="mt-8 rounded-full bg-blue-700 hover:bg-blue-600 text-white px-8 py-4 font-medium transition-colors"
              data-testid="about-cta-btn"
            >
              Persönlich beraten lassen
            </button>
          </div>
        </div>
      </section>

      {/* Auszeichnung */}
      <section className="bg-slate-50 py-24 sm:py-28 border-t border-slate-100" data-testid="award-section">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative mx-auto max-w-sm">
              <div className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-amber-100/70 to-blue-100/50 blur-2xl" aria-hidden="true" />
              <img
                src="/auszeichnung-unternehmerin.webp"
                alt="Auszeichnung Unternehmerin der Zukunft – Selma Schneider, EED Vertriebs GmbH"
                className="relative w-full rounded-3xl shadow-[0_40px_80px_-40px_rgba(0,0,0,0.35)] border border-amber-100"
                loading="lazy"
                data-testid="award-image"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-blue-700 mb-5 inline-flex items-center gap-2">
                <Trophy className="h-4 w-4" /> Ausgezeichnet
              </p>
              <h2 className="text-3xl sm:text-4xl font-heading font-semibold text-navy-900 leading-[1.1] mb-6">
                „Unternehmerin der <span className="font-serif italic text-blue-700">Zukunft</span>"
              </h2>
              <div className="space-y-4 text-slate-600 leading-relaxed">
                <p>
                  Selma Schneider, Geschäftsführerin der <strong className="text-navy-900">EED Vertriebs GmbH</strong>, wurde vom
                  Deutschen Innovationsinstitut (diind) in Kooperation mit DUP Unternehmer als
                  „Unternehmerin der Zukunft" ausgezeichnet – für proaktives, engagiertes und zukunftsfähiges Unternehmertum.
                </p>
                <p>
                  Als Energietarifberaterin im Auftrag der EED Vertriebs GmbH stehe ich für genau diese Werte:
                  eine unabhängige, ehrliche und persönliche Beratung, die Ihren langfristigen Vorteil in den Mittelpunkt stellt.
                </p>
              </div>
              <ul className="mt-8 space-y-3">
                {["Über 15 Jahre Erfahrung in Energieberatung & Tarifoptimierung", "Betreuung in ganz Deutschland", "Persönlich, ehrlich, unabhängig"].map((t, i) => (
                  <li key={i} className="flex items-start gap-3 text-navy-900" data-testid={`award-point-${i}`}>
                    <span className="mt-0.5 inline-grid place-items-center h-6 w-6 rounded-full bg-blue-50 text-blue-700 shrink-0"><Check className="h-3.5 w-3.5" strokeWidth={3} /></span>
                    <span className="text-sm leading-relaxed">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-24 border-t border-slate-100">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid md:grid-cols-3 gap-6">
          {VALUES.map((v, i) => (
            <Reveal key={i} delay={i * 0.1}>
              <div className="rounded-3xl border border-slate-200 p-10 h-full" data-testid={`value-${i}`}>
                <v.icon className="h-8 w-8 text-blue-700 mb-5" />
                <h3 className="text-xl font-heading text-navy-900 mb-3">{v.title}</h3>
                <p className="text-slate-500 leading-relaxed text-sm">{v.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <FaqSection />
    </>
  );
}
